import DataTable from "react-data-table-component";

import { Helmet } from "react-helmet"
import { useContext, useState } from "react"
import toast from "react-hot-toast";

import Modal from "./Modal";
import TicketView from "./TicketView";
import UpdateTicketForm from "./UpdateTicketForm";
import AddTicketForm from "./AddTicketForm";
import ConfirmPrompt from "./ConfirmPrompt";
import { AuthContext } from "../contexts/AuthContext";

import supportTicketService from "../services/supportTicketService";
import TableLoader from "./TableLoader";
import useSupportTickets from "../hooks/useSupportTickets";
import SectionHeader from "./SectionHeader";
import { formatDateToSydneyTimeZone } from "../utils/dateUtils";

const statusColors = {
  'Open': 'bg-ase-blue',
  'Closed': 'bg-green-700',
}

const UserSupport = () => {
  const { user } = useContext(AuthContext);

  const [selectedTicket, setSelectedTicket] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { data, isLoading, mutate } = useSupportTickets(
    { userId: user._id, page, perPage },
    { refreshInterval: 5 * 1000 }
  );

  const supportTickets = data?.supportTickets;
  const supportTicketCount = data?.count;

  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const [isUpdateTicketModalOpen, setIsUpdateTicketModalOpen] = useState(false);
  const [isViewTicketModalOpen, setIsTicketViewModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleUpdateClicked = () => {
    setIsUpdateTicketModalOpen(true);
  }

  const handleCloseViewTicket = () => {
    setIsTicketViewModalOpen(false);
  }

  const onDeleteConfirmed = () => {
    console.log("Delete confirmed");
  }

  const handleCreateTicket = async (ticket) => {
    try {
      setIsCreateTicketModalOpen(false);

      await supportTicketService.createSupportTicket({
        user: user._id,
        ...ticket
      });

      mutate();

      toast.success('Ticket created');
    } catch (error) {
      toast.error('An error occurred while creating the ticket');
    }
  }

  const handleUpdateTicket = async (ticketData) => {
    try {
      setIsUpdateTicketModalOpen(false);
      setIsTicketViewModalOpen(false);

      await supportTicketService.updateSupportTicket({
        ticketId: selectedTicket._id,
        ...ticketData
      });

      mutate();

      toast.success('Ticket updated');
    } catch (error) {
      toast.error('An error occurred while updating the ticket');
    }
  }

  const columns = [
    {
      name: "Title",
      selector: (row) => row.title,
      grow: 2,
    },
    {
      name: "Status",
      selector: (row) => row.status
    },
    {
      name: "Priority",
      selector: (row) => row.priority
    },
    {
      name: "Date",
      selector: (row) => row.createdAt,
      format: (row) => formatDateToSydneyTimeZone(row.createdAt)
    }
  ];

  return (
    <>
      <Helmet>
        <title>Support | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Tickets'
          actionsLeft={
            <button
              onClick={() => setIsCreateTicketModalOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>
          }
        />

        <DataTable
          columns={columns}
          data={supportTickets}
          striped
          highlightOnHover
          pagination
          paginationServer
          pointerOnHover
          onRowClicked={(row) => {
            setSelectedTicket(row);
            setIsTicketViewModalOpen(true);
          }}
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          paginationTotalRows={supportTicketCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </div>

      <Modal isOpen={isCreateTicketModalOpen}>
        <AddTicketForm
          onSubmit={handleCreateTicket}
          onCloseClicked={e => setIsCreateTicketModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isUpdateTicketModalOpen}>
        <UpdateTicketForm
          ticket={selectedTicket}
          onSubmit={handleUpdateTicket}
          onCloseClicked={e => setIsUpdateTicketModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isViewTicketModalOpen}>
        <TicketView
          ticketId={selectedTicket?._id}
          onUpdateClicked={handleUpdateClicked}
          onDeleteClicked={setIsConfirmDeleteModalOpen}
          onCloseClicked={handleCloseViewTicket}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete the ${selectedTicket?.issue} ticket?`}
          onConfirm={() => onDeleteConfirmed()}
          onCancel={() => setIsConfirmDeleteModalOpen(null)}
        />
      </Modal>
    </>
  )
}

export default UserSupport;
